import { z } from 'zod';

export const cdlitBookingReconciliationReasonSchema = z.enum([
  'duplicate_record_found',
  'no_record_found',
  'manual_yclients_check',
  'false_positive',
  'other',
]);

export type CdlitBookingReconciliationReason = z.infer<typeof cdlitBookingReconciliationReasonSchema>;

/** Allowlisted operational note codes — no free text. */
export const cdlitBookingReconciliationNoteCodeSchema = z.enum([
  'checked_yclients_ui',
  'checked_owner_call',
  'duplicate_in_crm',
  'no_record_in_crm',
  'escalated_support',
]);

export type CdlitBookingReconciliationNoteCode = z.infer<typeof cdlitBookingReconciliationNoteCodeSchema>;

export const cdlitBookingReconciliationStatusSchema = z.enum(['pending', 'reconciled']);

export type CdlitBookingReconciliationStatus = z.infer<typeof cdlitBookingReconciliationStatusSchema>;

export const cdlitAiOpsPeriodSchema = z.enum(['today', '7d']);

export type CdlitAiOpsPeriod = z.infer<typeof cdlitAiOpsPeriodSchema>;

export const cdlitAiOpsMetricsViewSchema = z
  .object({
    period: cdlitAiOpsPeriodSchema,
    started: z.number().int().min(0),
    recommendationShown: z.number().int().min(0),
    bookingPrepareCreated: z.number().int().min(0),
    bookingConfirmed: z.number().int().min(0),
    handoffCreated: z.number().int().min(0),
    slotConflict: z.number().int().min(0),
    outcomeUnknown: z.number().int().min(0),
    conversionRate: z.number().min(0).max(1).nullable(),
  })
  .strict();

export type CdlitAiOpsMetricsView = z.infer<typeof cdlitAiOpsMetricsViewSchema>;

export const cdlitAiOpsReconciliationItemSchema = z
  .object({
    attemptId: z.string().uuid(),
    status: z.string(),
    failureCode: z.string().nullable(),
    reconciliationStatus: cdlitBookingReconciliationStatusSchema.nullable(),
    reconciliationReason: cdlitBookingReconciliationReasonSchema.nullable(),
    serviceExternalId: z.string(),
    staffExternalId: z.string(),
    slotStartAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    reconciledAt: z.string().datetime().nullable(),
  })
  .strict();

export type CdlitAiOpsReconciliationItem = z.infer<typeof cdlitAiOpsReconciliationItemSchema>;

export const cdlitAiOpsHandoffItemSchema = z
  .object({
    handoffId: z.string().uuid(),
    sessionId: z.string().uuid(),
    reason: z.string(),
    createdAt: z.string().datetime(),
  })
  .strict();

export type CdlitAiOpsHandoffItem = z.infer<typeof cdlitAiOpsHandoffItemSchema>;

export const cdlitAiOpsReadinessCheckSchema = z
  .object({
    id: z.string(),
    ok: z.boolean(),
    message: z.string(),
  })
  .strict();

export const cdlitAiOpsReadinessViewSchema = z
  .object({
    ready: z.boolean(),
    readyForDryRun: z.boolean(),
    checks: z.array(cdlitAiOpsReadinessCheckSchema),
  })
  .strict();

export type CdlitAiOpsReadinessView = z.infer<typeof cdlitAiOpsReadinessViewSchema>;

export const cdlitAiOpsReconcileRequestSchema = z
  .object({
    reason: cdlitBookingReconciliationReasonSchema,
    noteCode: cdlitBookingReconciliationNoteCodeSchema.optional(),
  })
  .strict();

export type CdlitAiOpsReconcileRequest = z.infer<typeof cdlitAiOpsReconcileRequestSchema>;
